import {
    arrow,
    autoUpdate,
    FloatingArrow,
    FloatingPortal,
    flip,
    offset,
    shift,
    size,
    useFloating,
    useMergeRefs,
} from '@floating-ui/react';
import { useOnClickOutside } from 'custom_hooks/useOnClickOutside';
import { MediaSize, useIsViewportAtMost } from 'custom_hooks/useWindowSize';
import {
    CSSProperties,
    forwardRef,
    ReactNode,
    Ref,
    RefObject,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import useResizeAware from 'react-resize-aware';
import { Popup, PopupAnchorRef } from 'shared/popup/Popup';
import {
    FullScreenPopupContent,
    PopupContent,
    PopupContentWrapper,
} from 'shared/triggered_popup/styled';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { kendoPopupZIndex } from 'styles/variables';
import { stopEvent } from 'utils/event';

export type TriggeredPopupProps = Readonly<{
    /**
     * The element the popup should be positioned relative to.
     */
    anchorRef: PopupAnchorRef;
    /**
     * The content of the popup.
     */
    children?: ReactNode;
    /**
     * If the popup content should have no padding (default to `false`).
     */
    noPadding?: boolean;
    style?: CSSProperties;
    /**
     * When true, the popup will match the width of the trigger element.
     * When false or undefined, the popup will size to its content.
     */
    matchWidth?: boolean;
    className?: string;
    onClose: () => void;
    /**
     * When true, clicking outside of popup will trigger `onClose` callback (defaults to `true`).
     */
    allowCloseOnClickOutside?: boolean;
    /**
     * Additional refs that should not trigger popup close when clicked (e.g., dropdown portals).
     */
    contentRefs?: RefObject<HTMLElement>[];
    /**
     * When true, popup content will be displayed in a full screen modal for small screen sizes (defaults to `false`).
     */
    allowFullScreen?: boolean;
    /**
     * Used to override if popup should be displayed in full screen mode (ignored with `allowFullScreen` is `false`).
     */
    isFullScreen?: boolean;
    /**
     * Optional header for when popup is display in full screen mode (ignored with `allowFullScreen` is `false`).
     */
    fullScreenHeader?: ReactNode;
    /**
     * Sets visibility of popup arrow (defaults to `true`).
     */
    showArrow?: boolean;
    /**
     * Placement of the popup relative to anchor (defaults to 'bottom').
     */
    placement?: 'top' | 'bottom' | 'left' | 'right';
    /**
     * Distance between the popup and the anchor element (defaults to 20).
     */
    offsetDistance?: number;
    ariaLabel?: string;
    'test-id': string;
}>;

const TriggeredPopup = forwardRef<HTMLDivElement, TriggeredPopupProps>(
    (
        {
            anchorRef,
            noPadding = false,
            allowCloseOnClickOutside = true,
            contentRefs = [],
            allowFullScreen,
            isFullScreen = false,
            fullScreenHeader,
            children,
            style,
            className,
            onClose,
            matchWidth,
            showArrow = true,
            placement = 'bottom',
            offsetDistance = 20,
            ariaLabel,
            'test-id': testId,
        },
        ref,
    ) => {
        const [resizeListener] = useResizeAware();
        const atMostSmallViewport = useIsViewportAtMost(MediaSize.small);
        const renderFullScreen = allowFullScreen && (atMostSmallViewport || isFullScreen);
        const arrowRef = useRef<SVGSVGElement>(null);
        const popupRef = useRef<HTMLDivElement>(null);
        const contentRef = useRef<HTMLDivElement>(null);

        const middleware = useMemo(() => {
            const baseMiddleware = [
                shift({ padding: 12 }),
                arrow({ element: arrowRef, padding: 0 }),
                offset({ mainAxis: offsetDistance }),
                flip(),
            ];

            if (matchWidth || style) {
                baseMiddleware.push(
                    size({
                        apply({ rects, elements }) {
                            let computedWidth;

                            if (matchWidth) {
                                const element = anchorRef.current;
                                const actualWidth =
                                    element && 'offsetWidth' in element
                                        ? element.offsetWidth
                                        : rects.reference.width;
                                computedWidth = `${actualWidth}px`;
                            }

                            Object.assign(elements.floating.style, {
                                ...(computedWidth && { width: computedWidth }),
                                ...style,
                            });
                        },
                    }),
                );
            }

            return baseMiddleware;
        }, [offsetDistance, matchWidth, style, anchorRef]);

        const { context, refs, floatingStyles } = useFloating({
            placement,
            whileElementsMounted: autoUpdate,
            strategy: 'fixed',
            middleware,
            open: true,
        });

        const finalPopupRef: Ref<HTMLDivElement | null> = useMergeRefs([
            popupRef,
            ref,
            refs.setFloating,
        ]);

        const { setReference } = refs;
        useLayoutEffect(() => {
            setReference(anchorRef.current);
        }, [setReference, anchorRef]);

        useOnClickOutside(
            [popupRef, anchorRef, ...contentRefs],
            (event) => {
                stopEvent(event);
                onClose();
            },
            allowCloseOnClickOutside && !renderFullScreen,
            true,
        );

        return renderFullScreen ? (
            <Popup
                ref={finalPopupRef}
                anchorRef={anchorRef}
                fullScreenHeader={fullScreenHeader}
                onClose={onClose}
                isFullScreen
                ariaLabel={ariaLabel}
                test-id={testId}
            >
                <FullScreenPopupContent
                    style={style}
                    className={className}
                    ref={contentRef}
                    $noPadding={noPadding}
                >
                    {children}
                </FullScreenPopupContent>
            </Popup>
        ) : (
            <FloatingPortal>
                <div
                    ref={finalPopupRef}
                    aria-label={ariaLabel}
                    data-testid={testId}
                    style={{
                        ...floatingStyles,
                        zIndex: kendoPopupZIndex,
                    }}
                >
                    <PopupContentWrapper
                        id="popup content"
                        style={{ ...style, zIndex: 1 }}
                        className={className}
                        ref={contentRef}
                    >
                        {resizeListener}
                        <PopupContent $noPadding={noPadding}>{children}</PopupContent>
                        {showArrow && (
                            <FloatingArrow
                                ref={arrowRef}
                                context={context}
                                width={28}
                                height={14}
                                fill={tokens.background.paperElevation8}
                                stroke={tokens.popup.arrowStroke}
                                strokeWidth={1}
                                style={{
                                    filter: 'drop-shadow(var(--popup-arrow-shadow))',
                                    zIndex: -1,
                                }}
                            />
                        )}
                    </PopupContentWrapper>
                </div>
            </FloatingPortal>
        );
    },
);

export { TriggeredPopup };

/**
 * Component used to wrap popup trigger elements that do not expose a `ref` prop.
 * Wrapper is displayed as inline block element.
 *
 * @example
 *
 * const {
 *     triggerRef,
 *     popupRef,
 *     isOpen,
 *     toggleOpen,
 *     close,
 * } = usePopup<HTMLDivElement>();
 *
 * <TriggerWrapper ref={triggerRef}>
 *     <Button type="button" onClick={() => setOpen(true)}>Show Popup</Button>
 * </TriggerWrapper>
 *
 * {isOpen && (
 *     <TriggeredPopup ref={popupRef} anchorRef={triggerRef} onClose={close}>
 *         Here is the popup content
 *     </TriggeredPopup>
 * )}
 */
export const TriggerWrapper = styled.div`
    display: inline-block;
    position: relative;
`;
